import axios from "axios";
import constants from "./constants";
import endpoints from "./endpoints";
import {
  resetLoginData,
  storeAccessTokenFromRefreshToken,
} from "../store/reducers/loginData";

const http = axios.create({
  baseURL: constants.API_BASE_ENDPOINT,
  paramsSerializer: {
    indexes: null, // Serialize arrays as repeated keys (e.g. key=val1&key=val2) for Express 5 compatibility
  },
});

let isRefreshing = false;
let isLoggingOut = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (accessToken, refreshError) => {
  const subscribers = refreshSubscribers;
  refreshSubscribers = [];
  subscribers.forEach((callback) => callback(accessToken, refreshError));
};

// Endpoints reachable without a session. A 401 from these is a credential
// error, not an expired session, so it must never clear the stored session.
const unauthenticatedPaths = [
  endpoints.auth.login,
  endpoints.auth.renewToken,
  endpoints.auth.confirmAccount,
  endpoints.auth.forgotPassword,
  endpoints.auth.resetPassword,
].map((path) => path.replace(/^\//, ""));

const isUnauthenticatedRequest = (config) => {
  const url = `${config?.baseURL || ""}${config?.url || ""}`;
  return unauthenticatedPaths.some((path) => url.includes(path));
};

// Statuses that mean the server explicitly rejected the refresh token.
// Anything else (no response, timeout, 5xx) is transient.
const sessionRejectedStatuses = [400, 401, 403, 422];

const readAuthHeader = (config) => {
  const headers = config?.headers;

  if (!headers) {
    return null;
  }

  const value =
    typeof headers.get === "function"
      ? headers.get("Authorization")
      : headers.Authorization || headers.authorization;

  return typeof value === "string" ? value.replace(/^Bearer\s+/i, "") : null;
};

const forceLogout = (store) => {
  // Parallel 401s must not each dispatch a logout and clear storage.
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  try {
    refreshSubscribers = [];
    localStorage.clear();
    sessionStorage.clear();
    store.dispatch(resetLoginData());
  } finally {
    isLoggingOut = false;
  }
};

export const setupInterceptors = (store) => {
  http.interceptors.request.use((request) => {
    const kindAdminAccessToken =
      store.getState().loginData?.data?.tokens?.accessToken;

    if (kindAdminAccessToken) {
      request.headers.Authorization = "Bearer " + kindAdminAccessToken;
    }

    return request;
  });

  http.interceptors.response.use(
    (response) => {
      return response;
    },

    async (error) => {
      const originalRequest = error.config;

      // `error.response` is undefined for network errors / timeouts, so this
      // must stay optional or the interceptor itself throws.
      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        originalRequest._retry ||
        isUnauthenticatedRequest(originalRequest)
      ) {
        return Promise.reject(error);
      }

      const tokens = store.getState().loginData?.data?.tokens;
      const refreshToken = tokens?.refreshToken;
      const currentAccessToken = tokens?.accessToken;
      const usedAccessToken = readAuthHeader(originalRequest);

      if (!refreshToken) {
        // Only tear down a session that actually exists.
        if (currentAccessToken) {
          forceLogout(store);
        }
        return Promise.reject(error);
      }

      // This request went out with a token that has since been replaced, so
      // retry it with the current one instead of renewing again.
      if (currentAccessToken && usedAccessToken !== currentAccessToken) {
        originalRequest._retry = true;
        originalRequest.headers.Authorization = "Bearer " + currentAccessToken;
        return http(originalRequest);
      }

      // Queue parallel 401s while one renew is in flight
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((accessToken, refreshError) => {
            if (refreshError || !accessToken) {
              reject(refreshError || error);
              return;
            }
            originalRequest._retry = true;
            originalRequest.headers.Authorization = "Bearer " + accessToken;
            resolve(http(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.patch(
          constants.API_BASE_ENDPOINT + endpoints.auth.renewToken,
          {
            userType: constants.USER_TYPE.ADMINISTRATOR,
            emailAddress:
              store.getState().loginData?.data?.user?.emailAddress,
            refreshToken,
          },
          // The API also accepts the current (expired is fine) access token to
          // identify the session, and returns 400 "Unable to identify the
          // session for this refresh token" asking for it when it is missing.
          currentAccessToken
            ? { headers: { Authorization: "Bearer " + currentAccessToken } }
            : undefined
        );

        const accessToken = response?.data?.tokens?.accessToken;

        if (!accessToken) {
          // The server answered but handed back no token: the session is gone.
          onRefreshed(null, error);
          forceLogout(store);
          return Promise.reject(error);
        }

        store.dispatch(storeAccessTokenFromRefreshToken(response.data));

        onRefreshed(accessToken);
        originalRequest.headers.Authorization = "Bearer " + accessToken;

        return http(originalRequest);
      } catch (refreshError) {
        // Keep the session on transient failures (offline, timeout, 5xx);
        // only a rejection of the refresh token itself logs the admin out.
        const sessionRejected = sessionRejectedStatuses.includes(
          refreshError?.response?.status
        );

        onRefreshed(null, refreshError);

        if (sessionRejected) {
          forceLogout(store);
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchGetRequest = async (endpoint, extraConfig = {}) => {
  try {
    return await http.get(endpoint, {
      ...extraConfig,
    });
  } catch (err) {
    throw err.response;
  }
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {any} payload - Required API inputs
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchPostRequest = async (endpoint, payload, extraConfig = {}) => {
  try {
    return await http.post(endpoint, payload, {
      headers: {
        'Accept-Encoding': "*",
      },
      ...extraConfig,
    });
  } catch (error) {
    throw error.response;
  };
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {any} payload - Required API inputs
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchMultipartPostRequest = async (
  endpoint,
  payload,
  extraConfig = {}
) => {
  try {
    return await http.post(endpoint, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      ...extraConfig,
    });
  } catch (error) {
    throw error.response;
  }
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {any} payload - Required API inputs
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchPutRequest = async (endpoint, payload, extraConfig = {}) => {
  try {
    return await http.put(endpoint, payload, { ...extraConfig });
  } catch (err) {
    throw err.response;
  }
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {any} payload - Required API inputs
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchMultipartPutRequest = async (
  endpoint,
  payload,
  extraConfig = {}
) => {
  try {
    return await http.put(endpoint, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      ...extraConfig,
    });
  } catch (error) {
    throw error.response;
  }
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {any} payload - Required API inputs
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchPatchRequest = async (
  endpoint,
  payload,
  extraConfig = {}
) => {
  try {
    return await http.patch(endpoint, payload, { ...extraConfig });
  } catch (error) {
    throw error.response;
  }
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {any} payload - Required API inputs
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchMultipartPatchRequest = async (
  endpoint,
  payload,
  extraConfig = {}
) => {
  try {
    return await http.patch(endpoint, payload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      ...extraConfig,
    });
  } catch (error) {
    throw error.response;
  }
};

/**
 *
 * @param {string} endpoint - Path to resource endpoint
 * @param {import("axios").AxiosRequestConfig} extraConfig - additional axios settings for the request
 * @returns {Promise<import("axios").AxiosResponse<any>>}
 */
export const fetchDeleteRequest = async (endpoint, extraConfig = {}) => {
  try {
    return await http.delete(endpoint, { ...extraConfig });
  } catch (err) {
    throw err.response;
  }
};
